'use client';

import { X } from 'phosphor-icons';
import { useMemo } from 'react';
import { c } from '../../../utils/tailwind-merge';
import type { FeedbackButtonProps } from './FeedbackButton.types';

export const FeedbackButton = ({
  cta,
  onClick,
  onClose,
  className = '',
  buttonAriaLabel,
  closeAriaLabel,
  vertical = false,
}: FeedbackButtonProps) => {
  const handleClose = () => {
    onClose();
  };

  const handleClick = () => {
    onClick();
  };

  const wrapperBase = c(
    'flex items-center',
    'rounded-t-lg overflow-hidden select-none',
    vertical ? '-rotate-90' : '',
    'group',
    className
  );

  const sizeClasses = c(
    'w-[153px] h-9',
    'md:w-[153px] md:h-12',
    'lg:w-[143px] lg:h-9'
  );

  const bgClasses = c('bg-blue text-white', 'hover:bg-blue-hover');

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const motionClasses = prefersReducedMotion
    ? ''
    : c(
        'transition-all duration-300',
        '[transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]',
        'origin-bottom',
        'hover:h-[46px]',
        'md:hover:h-[58px]',
        'lg:hover:h-[46px]'
      );

  const labelAreaClass = c(
    'flex items-start justify-start',
    'w-[105px] h-full',
    'pl-3 pr-0 pt-1.5',
    'font-bold uppercase tracking-[0.094rem] text-white',
    'select-none'
  );

  const containerGapClass = c('gap-0.5', 'h-full', 'items-start');

  const closeBtnClass = c(
    'inline-flex items-start justify-center',
    'w-9 h-full',
    'p-2.5'
  );

  const transformStyle = prefersReducedMotion
    ? { transition: 'none' }
    : undefined;

  return (
    <div
      className={c(wrapperBase, sizeClasses, bgClasses, motionClasses)}
      data-test={'feedback-button'}
      style={transformStyle}
    >
      <div className={c('flex items-center', containerGapClass)}>
        <button
          type="button"
          onClick={handleClick}
          aria-label={buttonAriaLabel}
          className={c('order-0 focus-outline bg-transparent', labelAreaClass)}
          data-test="feedback-button-cta"
        >
          <span>{cta}</span>
        </button>
        <button
          type="button"
          onClick={handleClose}
          aria-label={closeAriaLabel}
          className={c('order-0 focus-outline bg-transparent', closeBtnClass)}
          data-test="feedback-button-close"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
};
